import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Webcam from "react-webcam";
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  StopCircle,
  Play,
  X,
  Clock,
  Brain,
  Loader2,
  ChevronLeft,
  ChevronRight,
  FileText,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Circle,
  Square,
  ZoomIn,
  ZoomOut,
  ExternalLink
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LiveSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  
  // Session state
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSlides, setLoadingSlides] = useState(true);
  
  // Slides state
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // PDF viewing state
  const [fileType, setFileType] = useState(null);
  const [originalFileUrl, setOriginalFileUrl] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null); // URL for PDF viewing (original or converted)
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfScale, setPdfScale] = useState(1.2);
  
  // Recording state
  const [recording, setRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [cameraOn, setCameraOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);
  
  // Presentation time limit (default 25 minutes, can be customized per user)
  const [presentationTimeLimit, setPresentationTimeLimit] = useState(25 * 60); // in seconds
  const [timeRemaining, setTimeRemaining] = useState(25 * 60); // countdown in seconds
  const countdownRef = useRef(null);
  
  // Presentation state
  const [presentationStarted, setPresentationStarted] = useState(false);
  
  // Q&A state
  const [qaMode, setQaMode] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [verbalAnalysis, setVerbalAnalysis] = useState([]);
  const [qaComplete, setQaComplete] = useState(false);
  
  // Voice recording state for Q&A
  const [isRecordingAnswer, setIsRecordingAnswer] = useState(false);
  const [answerTimer, setAnswerTimer] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState("");
  const answerMediaRecorderRef = useRef(null);
  const answerChunksRef = useRef([]);
  const answerTimerRef = useRef(null);
  const MAX_ANSWER_TIME = 60; // 60 seconds max
  
  // Analysis state
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    fetchSession();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [id]);

  const fetchSession = async () => {
    try {
      const response = await axios.get(`${API}/sessions/${id}`);
      setSession(response.data);
      
      // Check if presentation file exists
      if (!response.data.presentation_file) {
        toast.error("Please upload a presentation file first");
        navigate(`/session/new`);
        return;
      }
      
      // Fetch user's presentation time limit
      try {
        const userResponse = await axios.get(`${API}/users/${response.data.user_id}`);
        const userTimeLimit = userResponse.data.presentation_time_limit || 25;
        setPresentationTimeLimit(userTimeLimit * 60); // Convert to seconds
        setTimeRemaining(userTimeLimit * 60);
      } catch (err) {
        console.error("Failed to fetch user time limit, using default");
      }
      
      // Fetch slides
      await fetchSlides();
    } catch (error) {
      toast.error("Session not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchSlides = async () => {
    setLoadingSlides(true);
    try {
      const response = await axios.get(`${API}/sessions/${id}/slides`);
      setSlides(response.data.slides || []);
      
      // Set PDF viewing info
      if (response.data.file_type) {
        setFileType(response.data.file_type);
        setOriginalFileUrl(`${API}/sessions/${id}/presentation-file`);
        
        // Use pdf_url for viewing if available (handles both original PDFs and converted PPTs)
        if (response.data.pdf_url) {
          setPdfUrl(`${API}${response.data.pdf_url.replace('/api', '')}`);
        } else if (response.data.file_type === 'pdf') {
          setPdfUrl(`${API}/sessions/${id}/presentation-file`);
        } else {
          // No PDF available - will fall back to slide images
          setPdfUrl(null);
        }
      }
      
      console.log("Slides response:", {
        file_type: response.data.file_type,
        pdf_url: response.data.pdf_url,
        has_pdf: response.data.has_pdf,
        slides_count: response.data.slides?.length
      });
    } catch (error) {
      console.error("Failed to fetch slides:", error);
    } finally {
      setLoadingSlides(false);
    }
  };

  // PDF handlers
  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  }, []);

  const changePdfPage = (offset) => {
    setPageNumber(prev => Math.min(Math.max(1, prev + offset), numPages || 1));
  };

  const handleDataAvailable = useCallback(({ data }) => {
    if (data.size > 0) {
      setRecordedChunks((prev) => [...prev, data]);
    }
  }, []);

  const startPresentation = () => {
    setPresentationStarted(true);
    setRecordedChunks([]);
    setTimeRemaining(presentationTimeLimit);
    
    const stream = webcamRef.current?.stream;
    if (!stream) {
      // Continue without recording if camera not available
      toast.warning("Camera not available - proceeding without video recording");
    } else {
      // Start recording if camera is available
      try {
        mediaRecorderRef.current = new MediaRecorder(stream, {
          mimeType: "video/webm"
        });
        mediaRecorderRef.current.addEventListener("dataavailable", handleDataAvailable);
        mediaRecorderRef.current.start();
        setRecording(true);
      } catch (error) {
        console.error("Failed to start recording:", error);
        toast.warning("Recording failed - proceeding without video");
      }
    }
    
    // Elapsed time counter
    timerRef.current = setInterval(() => {
      setTimer((prev) => prev + 1);
    }, 1000);
    
    // Countdown timer - auto-stop when time runs out
    countdownRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // Time's up - auto stop and go to Q&A
          toast.warning("Time's up! Moving to Q&A session...");
          stopPresentation();
          return 0;
        }
        // Warning at 5 minutes remaining
        if (prev === 300) {
          toast.warning("5 minutes remaining!");
        }
        // Warning at 1 minute remaining
        if (prev === 60) {
          toast.error("1 minute remaining!");
        }
        return prev - 1;
      });
    }, 1000);
    
    toast.success(`Presentation started! You have ${Math.floor(presentationTimeLimit / 60)} minutes.`);
  };

  const stopPresentation = async () => {
    // Stop recording if active
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
    
    // Clear timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    
    // Wait for MediaRecorder cleanup if needed
    await new Promise(resolve => setTimeout(resolve, 500));
    
    toast.success("Presentation stopped. Starting Q&A session...");
    
    // Always proceed to Q&A session
    await startQASession();
  };

  const startQASession = async () => {
    // Set Q&A mode FIRST to trigger UI change
    setQaMode(true);
    
    // Fetch questions from AI
    try {
      const response = await axios.post(`${API}/sessions/${id}/qa`);
      const generatedQuestions = response.data.questions || [];
      
      if (generatedQuestions.length > 0) {
        setQuestions(generatedQuestions);
        setAnswers(new Array(generatedQuestions.length).fill(""));
        setVerbalAnalysis(new Array(generatedQuestions.length).fill(null));
      } else {
        // Fallback if no questions generated
        const fallbackQuestions = [
          "What is the main objective of your presentation?",
          "How would you handle questions about this topic from a skeptical audience?",
          "What are the key takeaways you want the audience to remember?"
        ];
        setQuestions(fallbackQuestions);
        setAnswers(["", "", ""]);
        setVerbalAnalysis([null, null, null]);
      }
    } catch (error) {
      console.error("Failed to generate Q&A:", error);
      // Use fallback questions on error
      const fallbackQuestions = [
        "What is the main objective of your presentation?",
        "How would you handle questions about this topic from a skeptical audience?",
        "What are the key takeaways you want the audience to remember?"
      ];
      setQuestions(fallbackQuestions);
      setAnswers(["", "", ""]);
      setVerbalAnalysis([null, null, null]);
    }
  };

  // Start voice recording for Q&A answer
  const startAnswerRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      answerChunksRef.current = [];
      answerMediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: "audio/webm"
      });
      
      answerMediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          answerChunksRef.current.push(e.data);
        }
      };
      
      answerMediaRecorderRef.current.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Process the recording
        await processAnswerRecording();
      };
      
      answerMediaRecorderRef.current.start();
      setIsRecordingAnswer(true);
      setAnswerTimer(0);
      setCurrentTranscript("");
      
      // Start timer
      answerTimerRef.current = setInterval(() => {
        setAnswerTimer((prev) => {
          if (prev >= MAX_ANSWER_TIME - 1) {
            // Auto-stop at max time
            stopAnswerRecording();
            return MAX_ANSWER_TIME;
          }
          return prev + 1;
        });
      }, 1000);
      
      toast.success("Recording started. Speak your answer clearly.");
      
    } catch (error) {
      console.error("Failed to start recording:", error);
      toast.error("Could not access microphone. Please check permissions.");
    }
  };

  // Stop voice recording
  const stopAnswerRecording = () => {
    if (answerMediaRecorderRef.current && isRecordingAnswer) {
      answerMediaRecorderRef.current.stop();
      setIsRecordingAnswer(false);
      
      if (answerTimerRef.current) {
        clearInterval(answerTimerRef.current);
        answerTimerRef.current = null;
      }
    }
  };

  // Process the recorded answer
  const processAnswerRecording = async () => {
    if (answerChunksRef.current.length === 0) {
      toast.error("No audio recorded");
      return;
    }
    
    setTranscribing(true);
    
    try {
      const blob = new Blob(answerChunksRef.current, { type: "audio/webm" });
      const file = new File([blob], `qa_answer_${currentQuestionIndex}.webm`, { type: "audio/webm" });
      
      const formData = new FormData();
      formData.append("file", file);
      formData.append("question_index", currentQuestionIndex);
      
      const response = await axios.post(
        `${API}/sessions/${id}/transcribe-qa`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      const { transcript, analysis } = response.data;
      
      // Update answers with transcript
      const newAnswers = [...answers];
      newAnswers[currentQuestionIndex] = transcript;
      setAnswers(newAnswers);
      setCurrentTranscript(transcript);
      
      // Store verbal analysis
      const newAnalysis = [...verbalAnalysis];
      newAnalysis[currentQuestionIndex] = analysis;
      setVerbalAnalysis(newAnalysis);
      
      toast.success("Answer recorded and analyzed!");
      
    } catch (error) {
      console.error("Transcription failed:", error);
      toast.error(error.response?.data?.detail || "Failed to transcribe. Please try again.");
    } finally {
      setTranscribing(false);
    }
  };

  // Move to next question after review
  const proceedToNextQuestion = () => {
    if (!answers[currentQuestionIndex]) {
      toast.error("Please record your answer first");
      return;
    }
    
    setCurrentTranscript("");
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setQaComplete(true);
      toast.success("Q&A complete! Ready to analyze.");
    }
  };

  const completeSession = async () => {
    setAnalyzing(true);
    
    try {
      if (recordedChunks.length > 0) {
        const blob = new Blob(recordedChunks, { type: "video/webm" });
        const file = new File([blob], `session_${id}.webm`, { type: "video/webm" });
        
        const formData = new FormData();
        formData.append("file", file);
        
        await axios.post(`${API}/sessions/${id}/upload-video`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      
      // Capture profile picture from webcam if this is user's first session
      // and they don't have a profile picture yet
      if (webcamRef.current && session?.user_id) {
        try {
          const userResponse = await axios.get(`${API}/users/${session.user_id}`);
          const user = userResponse.data;
          
          // Only capture if user doesn't have a profile picture and has completed 0 sessions
          if (!user.profile_picture && user.sessions_completed === 0) {
            const imageSrc = webcamRef.current.getScreenshot();
            if (imageSrc) {
              // Convert base64 to blob
              const response = await fetch(imageSrc);
              const imageBlob = await response.blob();
              const imageFile = new File([imageBlob], `profile_${session.user_id}.jpg`, { type: "image/jpeg" });
              
              const profileFormData = new FormData();
              profileFormData.append("file", imageFile);
              
              await axios.post(`${API}/users/${session.user_id}/capture-profile-picture`, profileFormData, {
                headers: { "Content-Type": "multipart/form-data" }
              });
              toast.success("Profile picture captured!");
            }
          }
        } catch (profileError) {
          console.log("Profile picture capture skipped:", profileError.message);
        }
      }
      
      // Submit Q&A answers with verbal analysis
      await axios.post(`${API}/sessions/${id}/qa-answers`, {
        questions: questions,
        answers: answers,
        verbal_analysis: verbalAnalysis.filter(a => a !== null)
      });
      
      await axios.post(`${API}/sessions/${id}/analyze`);
      
      toast.success("Analysis complete!");
      navigate(`/session/${id}/feedback`);
      
    } catch (error) {
      console.error("Analysis failed:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze session");
    } finally {
      setAnalyzing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const exitSession = () => {
    if (recording) {
      if (!window.confirm("Recording in progress. Are you sure you want to exit?")) {
        return;
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }
    }
    navigate("/dashboard");
  };

  const nextSlide = () => {
    if (fileType === 'pdf' && numPages) {
      if (pageNumber < numPages) {
        setPageNumber(pageNumber + 1);
      }
    } else {
      const maxSlide = slides.length > 0 ? slides.length - 1 : 4;
      if (currentSlide < maxSlide) {
        setCurrentSlide(currentSlide + 1);
      }
    }
  };

  const prevSlide = () => {
    if (fileType === 'pdf') {
      if (pageNumber > 1) {
        setPageNumber(pageNumber - 1);
      }
    } else {
      if (currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      }
    }
  };

  // Prioritize slides.length when available (more reliable than PDF page count)
  const totalSlides = slides.length > 0 ? slides.length : (fileType === 'pdf' ? (numPages || 1) : 5);
  const currentSlideNumber = currentSlide + 1;

  if (loading) {
    return (
      <div className="recording-container flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Q&A Mode View - Voice Based
  if (qaMode) {
    return (
      <div className="recording-container">
        <div className="absolute top-0 left-0 right-0 z-10 p-4 flex items-center justify-between glass-dark">
          <div className="flex items-center gap-4">
            <MessageSquare className="w-6 h-6 text-white" />
            <h1 className="text-white font-semibold text-lg">
              Q&A Session - {session?.topic_name}
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white text-sm">
              Question {currentQuestionIndex + 1} of {questions.length}
            </span>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          {!qaComplete ? (
            <div className="max-w-2xl w-full space-y-8">
              {/* Question Card */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <div className="glass rounded-2xl p-6 flex-1">
                  <p className="text-xs text-[#5C5C5C] uppercase tracking-wider mb-2">EVOLVE AI asks:</p>
                  <p className="text-lg text-[#1A1A1A] font-medium">
                    {questions[currentQuestionIndex]}
                  </p>
                </div>
              </div>

              {/* Voice Recording Section */}
              <div className="space-y-4">
                {/* Timer Progress Bar */}
                {isRecordingAnswer && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm text-white">
                      <span>Recording...</span>
                      <span>{formatTime(answerTimer)} / {formatTime(MAX_ANSWER_TIME)}</span>
                    </div>
                    <Progress 
                      value={(answerTimer / MAX_ANSWER_TIME) * 100} 
                      className="h-2 bg-white/20"
                    />
                  </div>
                )}
                
                {/* Recording Controls */}
                <div className="glass rounded-2xl p-8 text-center">
                  {transcribing ? (
                    // Transcribing state
                    <div className="space-y-4">
                      <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto" />
                      <p className="text-[#1A1A1A] font-medium">Transcribing your answer...</p>
                      <p className="text-sm text-[#5C5C5C]">AI is analyzing your response</p>
                    </div>
                  ) : currentTranscript ? (
                    // Show transcript after recording
                    <div className="space-y-4">
                      <CheckCircle className="w-12 h-12 text-[#059669] mx-auto" />
                      <p className="text-sm text-[#5C5C5C] uppercase tracking-wider">Your Answer:</p>
                      <div className="bg-white/50 rounded-xl p-4 max-h-40 overflow-y-auto">
                        <p className="text-[#1A1A1A] text-left">{currentTranscript}</p>
                      </div>
                      {verbalAnalysis[currentQuestionIndex] && (
                        <div className="grid grid-cols-3 gap-4 mt-4">
                          <div className="bg-primary/10 rounded-lg p-3">
                            <p className="text-2xl font-bold text-primary">
                              {verbalAnalysis[currentQuestionIndex].confidence_score}
                            </p>
                            <p className="text-xs text-[#5C5C5C]">Confidence</p>
                          </div>
                          <div className="bg-[#059669]/10 rounded-lg p-3">
                            <p className="text-2xl font-bold text-[#059669]">
                              {verbalAnalysis[currentQuestionIndex].preparedness_score}
                            </p>
                            <p className="text-xs text-[#5C5C5C]">Preparedness</p>
                          </div>
                          <div className="bg-[#2563EB]/10 rounded-lg p-3">
                            <p className="text-2xl font-bold text-[#2563EB]">
                              {verbalAnalysis[currentQuestionIndex].clarity_score}
                            </p>
                            <p className="text-xs text-[#5C5C5C]">Clarity</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : isRecordingAnswer ? (
                    // Recording state
                    <div className="space-y-4">
                      <div className="relative">
                        <div className="w-24 h-24 rounded-full bg-red-500/20 flex items-center justify-center mx-auto animate-pulse">
                          <div className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center">
                            <Mic className="w-8 h-8 text-white" />
                          </div>
                        </div>
                        {/* Sound wave animation */}
                        <div className="flex justify-center gap-1 mt-4">
                          {[...Array(5)].map((_, i) => (
                            <div
                              key={i}
                              className="w-1 bg-red-500 rounded-full animate-pulse"
                              style={{
                                height: `${Math.random() * 20 + 10}px`,
                                animationDelay: `${i * 0.1}s`
                              }}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-[#1A1A1A] font-medium">Listening to your answer...</p>
                      <p className="text-sm text-[#5C5C5C]">Speak clearly and confidently</p>
                    </div>
                  ) : (
                    // Ready to record state
                    <div className="space-y-4">
                      <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                        <Mic className="w-10 h-10 text-primary" />
                      </div>
                      <p className="text-[#1A1A1A] font-medium">Ready to record your answer</p>
                      <p className="text-sm text-[#5C5C5C]">
                        Click the button below and speak your answer (max {MAX_ANSWER_TIME} seconds)
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex justify-center gap-4">
                  {!transcribing && !currentTranscript && (
                    <>
                      {!isRecordingAnswer ? (
                        <Button
                          onClick={startAnswerRecording}
                          data-testid="start-recording-btn"
                          className="bg-[#059669] hover:bg-[#059669]/90 rounded-full px-8 py-6 text-lg"
                        >
                          <Circle className="w-5 h-5 mr-2 fill-current" />
                          Start Recording
                        </Button>
                      ) : (
                        <Button
                          onClick={stopAnswerRecording}
                          data-testid="stop-recording-btn"
                          className="bg-red-500 hover:bg-red-600 rounded-full px-8 py-6 text-lg animate-pulse"
                        >
                          <Square className="w-5 h-5 mr-2 fill-current" />
                          Stop Recording
                        </Button>
                      )}
                    </>
                  )}
                  
                  {currentTranscript && !transcribing && (
                    <div className="flex gap-4">
                      <Button
                        onClick={() => {
                          setCurrentTranscript("");
                          const newAnswers = [...answers];
                          newAnswers[currentQuestionIndex] = "";
                          setAnswers(newAnswers);
                        }}
                        variant="outline"
                        className="rounded-full px-6 bg-white/10 border-white/20 text-white hover:bg-white/20"
                      >
                        <Mic className="w-4 h-4 mr-2" />
                        Re-record
                      </Button>
                      <Button
                        onClick={proceedToNextQuestion}
                        data-testid="next-question-btn"
                        className="bg-primary hover:bg-primary/90 rounded-full px-8"
                      >
                        {currentQuestionIndex < questions.length - 1 ? (
                          <>Next Question <ChevronRight className="w-4 h-4 ml-2" /></>
                        ) : (
                          <>Complete Q&A <CheckCircle className="w-4 h-4 ml-2" /></>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Progress dots */}
              <div className="flex gap-2 justify-center">
                {questions.map((_, idx) => (
                  <div
                    key={idx}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      answers[idx] ? 'bg-[#059669]' :
                      idx === currentQuestionIndex ? 'bg-primary' :
                      'bg-white/30'
                    }`}
                  />
                ))}
              </div>
            </div>
          ) : (
            // Q&A Complete State
            <div className="text-center space-y-6">
              {analyzing ? (
                <>
                  <Loader2 className="w-16 h-16 text-white animate-spin mx-auto" />
                  <p className="text-xl text-white font-medium">Analyzing your presentation...</p>
                  <p className="text-white/70">Our AI is reviewing your performance and verbal answers</p>
                </>
              ) : (
                <>
                  <CheckCircle className="w-16 h-16 text-[#059669] mx-auto" />
                  <p className="text-xl text-white font-medium">Q&A Complete!</p>
                  <p className="text-white/70">Ready to analyze your full session including verbal responses</p>
                  
                  {/* Summary of verbal analysis */}
                  {verbalAnalysis.filter(a => a).length > 0 && (
                    <div className="glass rounded-2xl p-6 max-w-md mx-auto">
                      <p className="text-sm text-[#5C5C5C] mb-4">Q&A Performance Preview</p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-2xl font-bold text-primary">
                            {Math.round(
                              verbalAnalysis.filter(a => a).reduce((sum, a) => sum + a.confidence_score, 0) / 
                              verbalAnalysis.filter(a => a).length
                            )}
                          </p>
                          <p className="text-xs text-[#5C5C5C]">Avg Confidence</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#059669]">
                            {Math.round(
                              verbalAnalysis.filter(a => a).reduce((sum, a) => sum + a.preparedness_score, 0) / 
                              verbalAnalysis.filter(a => a).length
                            )}
                          </p>
                          <p className="text-xs text-[#5C5C5C]">Avg Preparedness</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-[#2563EB]">
                            {Math.round(
                              verbalAnalysis.filter(a => a).reduce((sum, a) => sum + a.clarity_score, 0) / 
                              verbalAnalysis.filter(a => a).length
                            )}
                          </p>
                          <p className="text-xs text-[#5C5C5C]">Avg Clarity</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    onClick={completeSession}
                    data-testid="analyze-session-btn"
                    className="bg-primary hover:bg-primary/90 rounded-full px-8 py-6 text-lg"
                  >
                    <Brain className="w-5 h-5 mr-2" />
                    Get AI Feedback
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Presentation Mode View
  return (
    <div className="recording-container">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-4 flex items-center justify-between glass-dark">
        <div className="flex items-center gap-4">
          <h1 className="text-white font-semibold text-lg truncate max-w-md">
            {session?.topic_name}
          </h1>
          {recording && (
            <div className="flex items-center gap-2 px-3 py-1 bg-red-500 rounded-full recording-indicator">
              <div className="w-2 h-2 bg-white rounded-full" />
              <span className="text-white text-sm font-medium">REC</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          {/* Countdown Timer - Main Display */}
          {presentationStarted && (
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
              timeRemaining <= 60 ? 'bg-red-500' : 
              timeRemaining <= 300 ? 'bg-[#D97706]' : 
              'bg-primary'
            }`}>
              <Clock className="w-5 h-5 text-white" />
              <span className="font-mono text-lg font-bold text-white">
                {formatTime(timeRemaining)}
              </span>
              <span className="text-white/70 text-sm">remaining</span>
            </div>
          )}
          {/* Elapsed Timer */}
          <div className="flex items-center gap-2 text-white/70">
            <span className="text-sm">Elapsed:</span>
            <span className="font-mono">{formatTime(timer)}</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            data-testid="exit-session-btn"
            onClick={exitSession}
            className="text-white hover:bg-white/10"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Slide Display Area */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 relative">
          {!presentationStarted ? (
            // Pre-presentation slide preview
            <div className="w-full max-w-4xl">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Preview Your Presentation
                </h2>
                <p className="text-white/70">
                  Review your {fileType === 'pdf' ? 'pages' : 'slides'}, then click &quot;Start Presentation&quot; when ready
                </p>
              </div>
              
              {/* Slide/PDF Preview */}
              <div className="aspect-video bg-white rounded-2xl shadow-2xl overflow-hidden relative">
                {loadingSlides ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-white">
                    <div className="text-center">
                      <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                      <p className="text-[#5C5C5C]">Processing presentation...</p>
                    </div>
                  </div>
                ) : slides.length > 0 ? (
                  // Prioritize slide images - more reliable than PDF viewer
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${slides[currentSlide]}`}
                    alt={`Slide ${currentSlide + 1}`}
                    className="w-full h-full object-contain bg-white"
                    data-testid="slide-image"
                    onError={(e) => {
                      console.log("Slide image failed to load:", slides[currentSlide]);
                      e.target.style.display = 'none';
                    }}
                  />
                ) : fileType === 'pdf' && pdfUrl ? (
                  // Fallback to PDF iframe if no slide images
                  <div className="w-full h-full flex items-center justify-center">
                    <iframe 
                      src={pdfUrl}
                      title="Presentation"
                      className="w-full h-full border-0"
                    />
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/5 to-accent/5">
                    <div className="text-center p-8">
                      <AlertCircle className="w-12 h-12 text-[#D97706] mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">
                        {fileType ? 'Preview not available for this format' : 'Slides are being processed'}
                      </h3>
                      <p className="text-sm text-[#5C5C5C]">
                        {originalFileUrl ? (
                          <button 
                            onClick={() => window.open(originalFileUrl, '_blank')}
                            className="text-primary underline"
                          >
                            Open presentation in new tab
                          </button>
                        ) : (
                          'Please wait while we extract your slides, or proceed with placeholder slides.'
                        )}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Slide Counter */}
                <div className="absolute bottom-4 right-4 px-3 py-1 bg-primary/90 rounded-full text-sm text-white font-medium">
                  {currentSlideNumber} / {totalSlides}
                </div>
                
                {/* PDF Zoom Controls */}
                {fileType === 'pdf' && (
                  <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/50 rounded-full p-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setPdfScale(prev => Math.max(prev - 0.2, 0.5))}
                      className="text-white hover:bg-white/20 rounded-full h-8 w-8 p-0"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </Button>
                    <span className="text-white text-xs px-2">{Math.round(pdfScale * 100)}%</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setPdfScale(prev => Math.min(prev + 0.2, 2.5))}
                      className="text-white hover:bg-white/20 rounded-full h-8 w-8 p-0"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Slide Navigation */}
              <div className="flex items-center justify-center gap-4 mt-6">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={prevSlide}
                  disabled={fileType === 'pdf' ? pageNumber <= 1 : currentSlide === 0}
                  data-testid="prev-slide-btn"
                  className="rounded-full bg-white/10 border-white/20 text-white hover:bg-white/20 disabled:opacity-30"
                >
                  <ChevronLeft className="w-6 h-6" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={nextSlide}
                  disabled={fileType === 'pdf' ? pageNumber >= (numPages || 1) : currentSlide === totalSlides - 1}
                  data-testid="next-slide-btn"
                  className="rounded-full bg-white/10 border-white/20 text-white hover:bg-white/20 disabled:opacity-30"
                >
                  Next
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </div>
              
              {/* Slide thumbnails (only for image slides, not PDF) */}
              {slides.length > 0 && fileType !== 'pdf' && (
                <div className="flex gap-2 justify-center mt-4 overflow-x-auto pb-2">
                  {slides.slice(0, 10).map((slide, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentSlide(idx)}
                      className={`flex-shrink-0 w-16 h-10 rounded-lg overflow-hidden border-2 transition-all ${
                        idx === currentSlide ? 'border-primary scale-110' : 'border-white/20 opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}${slide}`}
                        alt={`Thumb ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Active presentation view
            <>
              <div className="w-full max-w-4xl aspect-video bg-white rounded-2xl shadow-2xl overflow-hidden relative">
                {slides.length > 0 ? (
                  // Prioritize slide images - more reliable than PDF viewer
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}${slides[currentSlide]}`}
                    alt={`Slide ${currentSlide + 1}`}
                    className="w-full h-full object-contain bg-white"
                    data-testid="slide-image-active"
                    onError={(e) => {
                      console.log("Slide image failed to load:", slides[currentSlide]);
                      e.target.style.display = 'none';
                    }}
                  />
                ) : fileType === 'pdf' && pdfUrl ? (
                  // Fallback to PDF iframe if no slide images
                  <div className="w-full h-full flex items-center justify-center">
                    <iframe 
                      src={pdfUrl}
                      title="Presentation"
                      className="w-full h-full border-0"
                    />
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/5 to-accent/5">
                    <div className="text-center p-8">
                      <FileText className="w-16 h-16 text-primary mx-auto mb-4" />
                      <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2">
                        {fileType === 'pdf' ? `Page ${pageNumber}` : `Slide ${currentSlide + 1}`}
                      </h2>
                      <p className="text-[#5C5C5C]">{session?.topic_name}</p>
                    </div>
                  </div>
                )}
                
                <div className="absolute bottom-4 right-4 px-3 py-1 bg-primary/90 rounded-full text-sm text-white font-medium">
                  {currentSlide + 1} / {totalSlides}
                </div>
              </div>

              <div className="flex items-center gap-4 mt-6">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={prevSlide}
                  disabled={currentSlide === 0}
                  data-testid="prev-slide-btn-active"
                  className="rounded-full bg-white/10 border-white/20 text-white hover:bg-white/20 disabled:opacity-30"
                >
                  <ChevronLeft className="w-6 h-6" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={nextSlide}
                  disabled={currentSlide === totalSlides - 1}
                  data-testid="next-slide-btn-active"
                  className="rounded-full bg-white/10 border-white/20 text-white hover:bg-white/20 disabled:opacity-30"
                >
                  Next
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Camera Preview */}
        <div className="w-80 p-4 flex flex-col">
          <div className="rounded-2xl overflow-hidden bg-zinc-800 aspect-video relative">
            {cameraOn ? (
              <Webcam
                ref={webcamRef}
                audio={micOn}
                muted={true}
                videoConstraints={{
                  width: 640,
                  height: 360,
                  facingMode: "user"
                }}
                screenshotFormat="image/jpeg"
                screenshotQuality={0.8}
                className="w-full h-full object-cover"
                data-testid="webcam-video"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-white">
                <VideoOff className="w-12 h-12 mb-2 opacity-50" />
                <p className="text-sm">Camera off</p>
              </div>
            )}
            
            <div className="absolute bottom-2 left-2 right-2 flex justify-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                data-testid="toggle-camera-btn"
                onClick={() => setCameraOn(!cameraOn)}
                className={`rounded-full w-10 h-10 ${!cameraOn ? 'bg-red-500 text-white' : 'bg-white/20 text-white'}`}
              >
                {cameraOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                data-testid="toggle-mic-btn"
                onClick={() => setMicOn(!micOn)}
                className={`rounded-full w-10 h-10 ${!micOn ? 'bg-red-500 text-white' : 'bg-white/20 text-white'}`}
              >
                {micOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          
          <p className="text-white/50 text-xs text-center mt-2">Your camera preview</p>
        </div>
      </div>

      {/* Controls */}
      <div className="recording-controls">
        {!presentationStarted ? (
          <Button
            size="lg"
            data-testid="start-presentation-btn"
            onClick={startPresentation}
            className="rounded-full px-10 h-14 bg-[#059669] hover:bg-[#059669]/90 text-white"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Presentation
          </Button>
        ) : (
          <Button
            size="lg"
            data-testid="stop-presentation-btn"
            onClick={stopPresentation}
            className="rounded-full px-10 h-14 bg-red-500 hover:bg-red-600 text-white recording-indicator"
          >
            <StopCircle className="w-5 h-5 mr-2" />
            Stop & Begin Q&A
          </Button>
        )}
      </div>
    </div>
  );
}
